/* =========================================================================
   Store Page — ilva-theme-enterprise
   StorePageInfo web component for individual store (bolighus) pages.
   Depends on store-locator.js (classic script, must be loaded first),
   which publishes its shared API on theme.storeLocator.
   ========================================================================= */

/* Guarded like store-locator.js so a second script tag can't redeclare, and
   so the const bindings below can shadow same-named globals leaked by
   store-locator.js's function declarations. Body indentation left flat. */
if (!customElements.get('store-page-info')) {

const {
  StorefrontClient,
  createMapAdapter,
  readComponentTranslations,
  hoursRows,
  renderAddress,
  buildDirectionsUrl,
  escapeHtml,
} = window.theme.storeLocator;

function renderHoursRows(rows) {
  return rows
    .map(
      (row) =>
        `<div class="store-page__hours-row text-body-small"><span class="store-page__hours-day">${escapeHtml(row.label)}:</span><span class="store-page__hours-time">${escapeHtml(row.time)}</span></div>`
    )
    .join('');
}

/* --- StorePageInfo Web Component --- */

class StorePageInfo extends HTMLElement {
  connectedCallback() {
    this.shopDomain = this.dataset.shopDomain;
    this.storefrontToken = this.dataset.storefrontToken;
    this.mapApiKey = this.dataset.mapApiKey;
    this.mapProvider = this.dataset.mapProvider || 'mapbox';
    this.locationId = this.dataset.locationId;
    this.markerColor = this.dataset.markerColor || '#000000';

    this.translations = readComponentTranslations(this);

    this.els = {
      map: this.querySelector('[data-store-map]'),
      mapWrapper: this.querySelector('[data-store-map-wrapper]'),
      loading: this.querySelector('[data-store-loading]'),
      error: this.querySelector('[data-store-error]'),
      content: this.querySelector('[data-store-content]'),
      address: this.querySelector('[data-store-address]'),
      directionsLink: this.querySelector('[data-directions-link]'),
      hoursBlock: this.querySelector('[data-store-hours-block]'),
      hours: this.querySelector('[data-store-hours]'),
      contactBlock: this.querySelector('[data-store-contact-block]'),
      phone: this.querySelector('[data-store-phone]'),
      pickupBlock: this.querySelector('[data-store-pickup-block]'),
      pickupHours: this.querySelector('[data-pickup-hours]'),
      pickupAddressWrap: this.querySelector('[data-pickup-address-wrap]'),
      pickupAddress: this.querySelector('[data-pickup-address]'),
      bolighusInfo: this.querySelector('[data-bolighus-info]'),
    };

    this.loadData();
  }

  setState(state) {
    this.dataset.state = state;
    if (this.els.loading) this.els.loading.hidden = state !== 'loading';
    if (this.els.error) this.els.error.hidden = state !== 'error';
    if (this.els.content) this.els.content.hidden = state !== 'ready';
  }

  showError() {
    if (this.els.error) this.els.error.textContent = this.translations.error_loading || '';
    this.setState('error');
  }

  async loadData() {
    this.setState('loading');

    if (!this.shopDomain || !this.storefrontToken || !this.locationId) {
      this.showError();
      return;
    }

    let store;
    try {
      const client = new StorefrontClient(this.shopDomain, this.storefrontToken);
      const locations = await client.fetchLocations();
      store = locations.find((loc) => loc.id?.split('/').pop() === String(this.locationId));
    } catch (e) {
      this.showError();
      return;
    }

    if (!store) {
      this.showError();
      return;
    }

    this.store = store;
    this.render(store);
    this.setState('ready');
    this.initMap(store);
  }

  render(store) {
    const rows = hoursRows(store.openingHours, this.translations, { includeDateEntries: false });
    this.renderAddressBlock(store);
    this.renderHoursBlock(rows);
    this.renderContactBlock(store);
    this.renderPickupBlock(store, rows);
    this.renderBolighusInfo(store);
  }

  renderAddressBlock(store) {
    if (this.els.address) this.els.address.innerHTML = renderAddress(store.address);

    if (this.els.directionsLink) {
      const directionsUrl = buildDirectionsUrl(store.address);
      if (directionsUrl) this.els.directionsLink.href = directionsUrl;
      this.els.directionsLink.hidden = !directionsUrl;
    }
  }

  renderHoursBlock(rows) {
    if (this.els.hours) this.els.hours.innerHTML = renderHoursRows(rows);
    if (this.els.hoursBlock) this.els.hoursBlock.hidden = rows.length === 0;
  }

  renderContactBlock(store) {
    const phone = store.address?.phone || '';
    if (this.els.phone) {
      if (phone) {
        this.els.phone.textContent = phone;
        this.els.phone.href = `tel:${phone.replace(/\s+/g, '')}`;
      }
      this.els.phone.hidden = !phone;
    }
    const hasStaticLinks = this.els.contactBlock?.querySelector('[data-contact-link]');
    if (this.els.contactBlock) this.els.contactBlock.hidden = !phone && !hasStaticLinks;
  }

  renderPickupBlock(store, rows) {
    if (this.els.pickupHours) this.els.pickupHours.innerHTML = renderHoursRows(rows);

    let pickupAddressHtml = '';
    if (store.pickupInStore) {
      pickupAddressHtml = renderAddress(store.address);
    } else if (store.pickupAddress) {
      pickupAddressHtml = escapeHtml(store.pickupAddress).replace(/\n/g, '<br>');
    }

    if (this.els.pickupAddress) this.els.pickupAddress.innerHTML = pickupAddressHtml;
    if (this.els.pickupAddressWrap) this.els.pickupAddressWrap.hidden = !pickupAddressHtml;
    if (this.els.pickupBlock) this.els.pickupBlock.hidden = rows.length === 0 && !pickupAddressHtml;
  }

  renderBolighusInfo(store) {
    if (!this.els.bolighusInfo) return;
    const items = store.bolighusInfo || [];
    this.els.bolighusInfo.innerHTML = items
      .map((item) => `<li class="store-page__info-item text-body-small">${escapeHtml(item)}</li>`)
      .join('');
    this.els.bolighusInfo.hidden = items.length === 0;
  }

  async initMap(store) {
    if (!this.els.map) return;
    if (!this.mapApiKey || store.lat == null || store.lng == null) {
      if (this.els.mapWrapper) this.els.mapWrapper.hidden = true;
      return;
    }

    try {
      this.mapAdapter = createMapAdapter(this.mapProvider, this.mapApiKey);
      await this.mapAdapter.initMap(this.els.map, { lat: store.lat, lng: store.lng, zoom: 14 });
      this.mapAdapter.addMarker(store.lat, store.lng, store, null, { color: this.markerColor });
    } catch (e) {
      if (this.els.mapWrapper) this.els.mapWrapper.hidden = true;
    }
  }
}

customElements.define('store-page-info', StorePageInfo);

}
